import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from "react-router-dom";
import { connect, useSelector } from "react-redux";
import { useIdleTimer } from "react-idle-timer";

import {
  getRegPath,
  getOnboardingPath,
  getDashboardPath,
  getSettingPath,
} from "./constants";
import { getRole, getUserData, saveFCMToken } from "./utils/localStorage";
import * as actions from "./store/actions";

import ScrollToTop from "./scrollToTop";

import RegistrationLayout from "./layout/Registration/RegistrationLayout";
import OnboardingLayout from "./layout/Onboarding/OnboardingLayout";

import SignIn from "./pages/Registration/SignIn/";
import ForgotPassword from "./pages/Registration/SignIn/ForgotPassword";
import ResetPassword from "./pages/Registration/SignIn/ResetPassword";
import SignUp from "./pages/Registration/SignUp/";
import VerifyEmail from "./pages/Registration/SignUp/VerifyEmail";

import StepOne from "./pages/Registration/StepOne/index";
import StepTwo from "./pages/Registration/StepTwo/";
import StepThree from "./pages/Registration/StepThree/";

import OBStepOne from "./pages/Onboarding/Physician/StepOne";
import OBStepTwo from "./pages/Onboarding/Physician/StepTwo";
import OBStepThree from "./pages/Onboarding/Physician/StepThree";
import OBStepFour from "./pages/Onboarding/Physician/StepFour";
import OBStepFive from "./pages/Onboarding/Physician/StepFive";
import OBStepSix from "./pages/Onboarding/Physician/StepSix";
import OBDoseSpot from "./pages/Onboarding/Physician/DoseSpot";
import OBDSignature from "./pages/Onboarding/Physician/Signature";

import OBPStepOne from "./pages/Onboarding/Patient/StepOne";
import OBPStepTwo from "./pages/Onboarding/Patient/StepTwo";
import OBContact from "./pages/Onboarding/Patient/ContactPatient";
import OBPProfileComplete from "./pages/Onboarding/Patient/StepThree";
import OBDoseSpotPatient from "./pages/Onboarding/Patient/DoseSpotPatient";

import CreateProfile from "./pages/Onboarding/CreateProfile";
import ProfileComplete from "./pages/Onboarding/ProfileComplete";

import DashboardLayout from "./layout/Dashboard/DashboardLayout";

import Appointments from "./pages/Dashboard/Appointments";
import Patients from "./pages/Dashboard/Patients";
import PatientProfile from "./pages/Dashboard/Patients/PatientProfile/index";
import Notifications from "./pages/Dashboard/notifications";
import AvailabilitySchedule from "./pages/Dashboard/AvailabilitySchedule";
import ChangePassword from "./pages/Dashboard/ChangePassword";
import NoteTemplates from "./pages/Dashboard/NoteTemplates";
import CreateNoteTemplate from "./pages/Dashboard/NoteTemplates/CreateNoteTemplate";
import ViewEditTemplate from "./pages/Dashboard/NoteTemplates/ViewEditTemplate";
import EarningReports from "./pages/Dashboard/EarningReports";

import BlockPatients from "./pages/Settings/BlockPatients";
import StartCall from "./pages/Dashboard/Appointments/StartCall";
import NudgePermission from "./pages/Settings/NudgePermissions";
import ChangeLocation from "./pages/Settings/ChangeLocation";
import PaymentHistory from "./pages/Settings/PaymentHistory";
import AboutUs from "./pages/Settings/AboutUs";
import ContactUs from "./pages/Settings/ContactUs";

import UserConfirmation from "./UserConfirmation";
import ShareProfile from "./pages/Dashboard/ShareProfile";
import UnderReview from "./pages/Onboarding/ProfileComplete/UnderReview";
import ConciergeService from "./pages/Dashboard/ConciergeService/ConciergeService";
import ConciergeServiceSettings from "./pages/Settings/ConciergeService"

import { getToken } from "./firebase";
import { errorHandler, successHandler } from "./store/actions/responseHandler";
import NotFound from "./pages/PageNotFound";

const AppRouter = ({
  isAuth,
  onAutoTryLogin,
  redirectPath,
  redirectToOnboardingPath,
  logout,
  setLoginData,
}) => {
  const [tokenFound, setTokenFound] = useState("");
  getToken(setTokenFound);

  const isEnableConciergeService = useSelector(state => state.dashboard.conciergeService.isEnableConciergeService);

  useEffect(() => {
    onAutoTryLogin();
    window.addEventListener("offline", () =>
      errorHandler("Network Disconnected")
    );
    window.addEventListener("online", () =>
      successHandler("Network Connected")
    );

    return () => {
      window.removeEventListener("online", () => { });
      window.removeEventListener("offline", () => { });
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    saveFCMToken(tokenFound);
  }, [tokenFound, setLoginData]);

  const handleOnIdle = event => {
    logout();
  };

  useIdleTimer(
    isAuth && {
      timeout: 1000 * 60 * 30,
      onIdle: handleOnIdle,
      debounce: 500,
    }
  );

  let routes;
  const userData = getUserData();

  let onboardingRoutes;
  let dashboardRoutes;

  if (!isAuth) {
    routes = (
      <Switch>
        <Route path={getRegPath("/")} exact component={StepOne} />
        <Route path={getRegPath("/step2")} component={StepTwo} />
        <Route path={getRegPath("/step3/:step([1-7])")} component={StepThree} />
        <Route path={getRegPath("/signin")} component={SignIn} />
        <Route path={getRegPath("/signup")} component={SignUp} />
        <Route path={getRegPath("/verify-email")} component={VerifyEmail} />
        <Route
          path={getRegPath("/forgot-password")}
          component={ForgotPassword}
        />
        <Route
          path={getRegPath("/auth/reset-password/:otp/:email")}
          component={ResetPassword}
        />
        <Redirect
          to={redirectPath ? getRegPath(redirectPath) : getRegPath("/")}
        />
      </Switch>
    );
  } else {
    onboardingRoutes =
      getRole() === "PHYSICIAN" ? (
        <ScrollToTop>
          <Switch>
            <Route
              exact
              path={getOnboardingPath("/under-review")}
              component={UnderReview}
            />
            <Route
              exact
              path={getOnboardingPath("/create-profile")}
              component={CreateProfile}
            />
            <Route
              exact
              path={getOnboardingPath("/step1")}
              component={OBStepOne}
            />
            <Route
              exact
              path={getOnboardingPath("/step2")}
              component={OBStepTwo}
            />
            <Route
              exact
              path={getOnboardingPath("/step3")}
              component={OBStepThree}
            />
            <Route
              exact
              path={getOnboardingPath("/step4")}
              component={OBStepFour}
            />
            <Route
              exact
              path={getOnboardingPath("/step5")}
              component={OBStepFive}
            />
            <Route
              exact
              path={getOnboardingPath("/step6")}
              component={OBStepSix}
            />
            <Route
              exact
              path={getOnboardingPath("/dosespot")}
              component={OBDoseSpot}
            />
            <Route
              exact
              path={getOnboardingPath("/uplaodsignature")}
              component={OBDSignature}
            />
            <Route
              exact
              path={getOnboardingPath("/profile-complete")}
              component={ProfileComplete}
            />
            <Redirect
              to={getOnboardingPath(
                redirectToOnboardingPath || "/create-profile"
              )}
            />
          </Switch>
        </ScrollToTop>
      ) : (
        <ScrollToTop>
          <Switch>
            <Route
              exact
              path={getOnboardingPath("/create-profile")}
              component={CreateProfile}
            />
            <Route
              exact
              path={getOnboardingPath("/step1")}
              component={OBPStepOne}
            />
            <Route
              exact
              path={getOnboardingPath("/contact")}
              component={OBContact}
            />
            <Route
              exact
              path={getOnboardingPath("/dosespotPatient")}
              component={OBDoseSpotPatient}
            />
            <Route
              exact
              path={getOnboardingPath("/step2")}
              component={OBPStepTwo}
            />
            <Route
              exact
              path={getOnboardingPath("/profile-complete")}
              component={OBPProfileComplete}
            />
            <Redirect
              to={getOnboardingPath(
                redirectToOnboardingPath || "/create-profile"
              )}
            />
          </Switch>
        </ScrollToTop>
      );
    dashboardRoutes = (
      <Switch>
        <Route
          path={getDashboardPath(getSettingPath("/blocked-patients"))}
          component={BlockPatients}
        />
        <Route
          exact
          path={getDashboardPath(getSettingPath("/nudge-permission"))}
          component={NudgePermission}
        />
        <Route
          exact
          path={getDashboardPath(getSettingPath("/concierge-service-settings"))}
          component={ConciergeServiceSettings}
        />
        <Route
          exact
          path={getDashboardPath(getSettingPath("/change-location"))}
          component={ChangeLocation}
        />
        <Route
          exact
          path={getDashboardPath(getSettingPath("/about-us"))}
          component={AboutUs}
        />
        <Route
          exact
          path={getDashboardPath(getSettingPath("/contact-us"))}
          component={ContactUs}
        />
        <Route
          exact
          path={getDashboardPath(getSettingPath("/payment-history"))}
          component={PaymentHistory}
        />

        <Route
          path={getDashboardPath("/appointments")}
          component={Appointments}
        />

        <Route
          path={getDashboardPath("/notifications")}
          component={Notifications}
        />
        <Route
          path={getDashboardPath("/change-password")}
          component={ChangePassword}
        />
        <Route
          exact
          path={getDashboardPath("/share-profile")}
          component={ShareProfile}
        />
        <Route
          exact
          path={getDashboardPath("/availability-schedule")}
          component={AvailabilitySchedule}
        />
        <Switch>
          <Route
            exact
            path={getDashboardPath("/note-templates")}
            component={NoteTemplates}
          />
          <Route
            exact
            path={getDashboardPath("/earning-reports")}
            component={EarningReports}
          />
          <Route
            exact
            path={getDashboardPath("/note-templates/create")}
            component={CreateNoteTemplate}
          />
          <Route
            exact
            path={getDashboardPath("/note-templates/:id")}
            component={ViewEditTemplate}
          />
          {isEnableConciergeService && 
          <Route
            exact
            path={getDashboardPath("/concierge-service")}
            component={ConciergeService}
          />}
          <Switch>
            <Route
              path={getDashboardPath("/patients/detail/:id")}
              component={PatientProfile}
            />
            <Route path={getDashboardPath("/patients")} component={Patients} />
          </Switch>
        </Switch>

        <Redirect
          to={getDashboardPath(redirectToOnboardingPath || "/appointments")}
        />
      </Switch>
    );
  }

  return (
    <Router
      getUserConfirmation={(message, callback) =>
        UserConfirmation(message, callback)
      }
    >
      <Switch>
        <Route path="/404" component={NotFound} />
        {
          (!userData?.onboarding && !isAuth) &&
          <Route
            path={getRegPath("")}
            component={() => <RegistrationLayout>{routes}</RegistrationLayout>}
          />
        }
        <Route
          path={getOnboardingPath("")}
          component={() => (
            <OnboardingLayout>{onboardingRoutes}</OnboardingLayout>
          )}
        />
        {
          (!userData?.onboarding && userData?.doctor_status != "PROFILE_INCOMPLETE") && <Switch>
            <Route
              exact
              path={getDashboardPath("/appointments/start-call")}
              component={StartCall}
            />
            <Route
              path={getDashboardPath("")}
              component={() => (
                <DashboardLayout>{dashboardRoutes}</DashboardLayout>
              )}
            />
            <Route component={() => <Redirect to={"/404"} />} />
          </Switch>
        }
        <Route component={() => <Redirect to={"/404"} />} />
      </Switch>
      {!isAuth &&
        redirectPath &&
        redirectPath === getRegPath("/signin") &&
        !window.location.pathname.includes("reset-password") && (
          <Redirect to={getRegPath("/")} />
        )}

      <Route
        exact
        path="/"
        render={() => (
          <Redirect
            to={
              !!localStorage.getItem("token")
                ? userData?.doctor_status !== "ACCEPTED"
                  ? getOnboardingPath("/")
                  : getDashboardPath("/appointments")
                : getRegPath("/")
            }
          />
        )}
      />
      {["PENDING", "DENIED"].includes(userData?.doctor_status) &&
        getRole() === "PHYSICIAN" && (
          <Redirect to={getOnboardingPath("/under-review")} />
        )}

      {userData?.doctor_status === "PROFILE_INCOMPLETE" &&
        getRole() === "PHYSICIAN" &&
        !localStorage.getItem("bankRedirect") && (
          <Redirect to={getOnboardingPath("/")} />
        )}
    </Router>
  );
};

const mapStoreToProps = ({ auth, onboarding }) => ({
  isAuth: auth.isAuth,
  redirectPath: auth.redirectPath,
  redirectToOnboardingPath: auth.redirectToOnboardingPath,
});

const mapDispatchToProps = dispatch => {
  return {
    onAutoTryLogin: () => dispatch(actions.setTryAutoLogin()),
    logout: () => dispatch(actions.logout()),
    setLoginData: updateData => dispatch(actions.setLoginData(updateData)),
  };
};

export default connect(mapStoreToProps, mapDispatchToProps)(AppRouter);
